import React, {useEffect} from "react";
import {UptimeMonitorForm} from "./UptimeMonitor";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter
} from "../components/ui/dialog";
import {Button} from "../components/ui/button";
import {IngestionSettings} from "./settings/ExclusionSettings";
import {ClusterSettings} from "./settings/ClusterSettings";
import {BaseView} from "./BaseView";
import {Tabs, TabsContent, TabsList, TabsTrigger} from "../components/ui/tabs";
import axios from "../utility/customAxios";
import {cn} from "../components/ui/lib/utils";
import {Input} from "../components/ui/input";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "../components/ui/select";
import {PagerDutyService} from "../components/Alert/AlertDestination";
import AuthContext from "../contexts/JWTContext";
import {Tooltip, TooltipContent, TooltipTrigger} from "../components/ui/tooltip";
import {InfoIcon, Webhook, XIcon} from "lucide-react";
import slackLogo from "../assets/images/slackLogo.png"
import pagerDuty from "../assets/images/pagerDuty.png"
import {PagerDutyServices} from "./PagerDutyRedirect";
import {DropDownItem} from "../components/Input/MultiSelectorDropdown/MultiSelectorDropDown";
import {SingleSelectDropdown} from "../components/Input/SingleSelectDropdown";
import {LoadingSpinner} from "../components/ui/customSpinner";
import {toast} from 'components/ui/use-toast';
import {Separator} from "../components/ui/separator";
import {StatusPageIntegration} from "../components/StatusPageIntegration";
import {EventIngestionIntegration} from "../components/EventIngestionIntegration";
import {Users} from "./settings/Users";
import {UsersAndPermissionsTab} from "./settings/Permissions";
import {useSearchParams} from "react-router-dom";
import { CheckType } from "../clients/metoro/uptime_monitor";
import {VerticalTabs} from "../components/ui/vertical-tabs";
import {useMetoroConfig} from "../hooks/useMetoroConfig";

interface UptimeMonitor {
    uuid: string;
    name: string;
    description?: string;
    checkType: CheckType;
    // HTTP fields
    endpoint: string;
    method: string;
    headers: { [key: string]: string };
    body: string;
    expectedStatusCode: string;
    responseBodyRegex?: string;
    // TCP fields
    host?: string;
    port?: number;
    // Common fields
    checkFrequencySeconds: number;
    responseTimeMetricName: string;
    statusMetricName: string;
    metricNameBase?: string;
}

function IntegrationsTab() {
    const {config} = useMetoroConfig();
    
    return (
        <div className="space-y-4 px-4">
            <UptimeMonitorIntegration/>
            <StatusPageIntegration/>
            <EventIngestionIntegration/>
            <GithubIntegration/>
            {!config?.isOnprem && <SlackIntegration/>}
            {!config?.isOnprem && <PagerDutyIntegration/>}
            <WebhookIntegration/>
        </div>
    );
}

function SlackIntegration() {
    const [slackIntegrationExists, setSlackIntegrationExists] = React.useState(true);
    const [slackShareToken, setSlackShareToken] = React.useState("")
    let host = window.location.host

    React.useEffect(() => {
        if (slackIntegrationExists) return
        axios.get("/api/v1/slack/shareToken").then((response) => {
            setSlackShareToken(response.data.token)
        }).catch((e) => {
            console.error(e)
            let errorMessage = "Failed to get slack share token";
            if (e.response && e.response.status === 403) {
                errorMessage = e.response.data.error;
            }
            toast({
                title: "Failed to Get Slack Token",
                description: errorMessage,
                variant: "destructive"
            });
        })
    }, [slackIntegrationExists])

    React.useEffect(() => {
        axios.get("/api/v1/integrationExists?type=slack").then((response) => {
            setSlackIntegrationExists(response.data.exists)
        }).catch((e) => {
            console.error(e)
            let errorMessage = "Failed to check slack integration";
            if (e.response && e.response.status === 403) {
                errorMessage = e.response.data.error;
            }
            toast({
                title: "Failed to Check Slack Integration",
                description: errorMessage,
                variant: "destructive"
            });
        })
    }, []);

    return (
        <div className={"mt-4 p-4 bg-backgroundlight border rounded"}>
            <div className={"flex"}>
                <div className={"flex-grow"}>
                    <div className={"flex col"}>
                        <img src={slackLogo} className={"w-8 h-6"}/>
                        <h3 className={"text-lg font-bold"}>Slack</h3>
                    </div>
                    <p className={"text-sm text-textdark"}>Integrate with Slack to allow Metoro to post to slack rooms
                        when alerts
                        fire.</p>
                </div>
                <div className={"h-full"}>
                    {
                        !slackIntegrationExists &&
                        <div className={"justify-center h-full"}>
                            <Button
                                requiredPermission={{
                                    resource: "integrations/default/*",
                                    operation: "create"
                                }}
                                className={cn("bg-primarytransparent h-full border border-primary text-white flex flex-col justify-center p-2 rounded")}
                                onClick={(e) => {
                                    window.location.href = "https://slack.com/oauth/v2/authorize?client_id=5445777126257.5568817569717&scope=channels:read,chat:write,chat:write.public&user_scope=&redirect_uri=https://" + host + "/slack-redirect&state=" + slackShareToken
                                }}
                            >Add to slack</Button>
                        </div>
                    }
                    {
                        slackIntegrationExists &&
                        <Button
                            requiredPermission={{
                                resource: "integrations/default/*",
                                operation: "delete"
                            }}
                            onClick={() => {
                                axios.post("/api/v1/integration/remove?type=slack").then(() => {
                                    setSlackIntegrationExists(false)
                                }).catch((e) => {
                                    console.error(e)
                                    let errorMessage = "Failed to remove slack integration";
                                    if (e.response && e.response.status === 403) {
                                        errorMessage = e.response.data.error;
                                    }
                                    toast({
                                        title: "Failed to Remove Slack Integration",
                                        description: errorMessage,
                                        variant: "destructive"
                                    });
                                })
                            }}
                            className={"bg-primarytransparent h-full border border-primary text-white flex flex-col justify-center p-2 rounded"}>Disconnect</Button>
                    }
                </div>
            </div>
        </div>
    );
}

function PagerDutyIntegration() {
    const [pagerDutyIntegrationExists, setPagerDutyIntegrationExists] = React.useState(true);
    const [pagerDutyShareToken, setPagerDutyShareToken] = React.useState("")
    const [disconnect, setDisconnect] = React.useState(false)
    const [isEditing, setIsEditing] = React.useState(false)
    const [openViewServices, setOpenViewServices] = React.useState(false)
    const [selectedServices, setSelectedServices] = React.useState<PagerDutyService[]>([])
    const [allServices, setAllServices] = React.useState<PagerDutyService[]>([])
    const [popoverOpen, setPopoverOpen] = React.useState(false)
    const [isSaved, setIsSaved] = React.useState(false)
    let host = window.location.host


    React.useEffect(() => {
        if (!isEditing) {
            return
        }
        axios.get("/api/v1/pagerDuty/services").then((response) => {
            setAllServices(response.data.services)
        }).catch((e) => {
            console.error(e)
        })
    }, [isEditing]);

    React.useEffect(() => {
        if (!isSaved) {
            return
        }
        if (selectedServices.length > 0) {
            axios.post("/api/v1/pagerDuty/selectedServices",
                {services: selectedServices}
            ).then((response) => {
                // Redirect to the dashboard
                setOpenViewServices(false)
                setIsSaved(false)
                setIsEditing(false)
                setPopoverOpen(false)
            }).catch((e) => {
                console.error(e)
            })
        }
    }, [isSaved]);

    React.useEffect(() => {
        if (pagerDutyIntegrationExists) return
        axios.get("/api/v1/pagerDuty/shareToken").then((response) => {
            setPagerDutyShareToken(response.data.token)
        }).catch((e) => {
            console.error(e)
            let errorMessage = "Failed to get PagerDuty share token";
            if (e.response && e.response.status === 403) {
                errorMessage = e.response.data.error;
            }
            toast({
                title: "Failed to Get PagerDuty Token",
                description: errorMessage,
                variant: "destructive"
            });
        })
    }, [pagerDutyIntegrationExists])


    React.useEffect(() => {
        axios.get("/api/v1/integrationExists?type=pagerDuty").then((response) => {
            setPagerDutyIntegrationExists(response.data.exists)
        }).catch((e) => {
            console.error(e)
            let errorMessage = "Failed to check PagerDuty integration";
            if (e.response && e.response.status === 403) {
                errorMessage = e.response.data.error;
            }
            toast({
                title: "Failed to Check PagerDuty Integration",
                description: errorMessage,
                variant: "destructive"
            });
        })
    }, []);

    React.useEffect(() => {
        axios.get("/api/v1/pagerDuty/selectedServices")
            .then((response) => {
                setSelectedServices(response.data.services)
            }).catch((e) => {
                console.error(e)
                let errorMessage = "Failed to get PagerDuty services";
                if (e.response && e.response.status === 403) {
                    errorMessage = e.response.data.error;
                }
                toast({
                    title: "Error",
                    description: errorMessage,
                    variant: "destructive"
                });
            })
    }, []);

    let protocol = window.location.protocol
    let pagerDutyClientId = "4f78f15d-8395-4302-9706-50e177dd75e7" //for production and dev.
    if (host.includes("localhost")) {
        pagerDutyClientId = "f583ce7e-d07b-4a24-95e9-b2c26a06a725"
    }

    return (
        <div className={"mt-4 p-4 bg-backgroundlight border rounded"}>
            <div className={"flex"}>
                <div className={"flex-grow"}>
                    <div className={"flex col"}>
                        <img src={pagerDuty} className={"w-7 h-7 mr-1"}/>
                        <h3 className={"text-lg font-bold"}>PagerDuty</h3>
                    </div>
                    <p className={"text-sm text-textdark"}>Integrate with PagerDuty to receive pages for Metoro
                        alerts.</p>
                </div>
                <div className={"h-full"}>
                    {
                        !pagerDutyIntegrationExists &&
                        <Button
                            requiredPermission={{
                                resource: "integrations/default/*",
                                operation: "create"
                            }}
                            className={cn("bg-primarytransparent h-full border border-primary text-white flex flex-col justify-center p-2 rounded")}
                            onClick={(e) => {
                                window.location.href = "https://identity.pagerduty.com/oauth/authorize?client_id=" + pagerDutyClientId + "&response_type=code&scope=write&redirect_uri=" + protocol + "//" + host + "/pagerduty-redirect&state=" + pagerDutyShareToken
                            }}
                        >Connect to PagerDuty</Button>
                    }
                    {
                        pagerDutyIntegrationExists &&
                        <div className={"flex gap-2 justify-center items-center"}>
                            <Dialog open={openViewServices}>
                                <DialogTrigger asChild>
                                    <Button
                                        requiredPermission={{
                                            resource: "integrations/default/*",
                                            operation: "read"
                                        }}
                                        onClick={() => setOpenViewServices(true)}
                                        className={"bg-primarytransparent h-full border border-primary text-white flex flex-col justify-center p-2 rounded hover:border-primaryhover"}>
                                        View Services
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="sm:max-w-[825px] bg-background"
                                               onInteractOutside={() => setOpenViewServices(false)}
                                               onKeyDown={(e) => {
                                                   if (e.key === "Escape") {
                                                       setOpenViewServices(false)
                                                   }
                                               }}>
                                    <DialogHeader>
                                        <div className={"flex justify-between items-center mb-4"}>
                                            <DialogTitle
                                                className={"text-textmedium font-normal text-left items-center"}>
                                                Integrated PagerDuty Services
                                            </DialogTitle>
                                            <XIcon
                                                className={"text-textdark hover:cursor-pointer hover:text-primary justify-end"}
                                                onClick={() => setOpenViewServices(false)}/>
                                        </div>
                                    </DialogHeader>
                                    <div className={"flex justify-between items-center"}>
                                        <DialogDescription className={"text-textdark"}>
                                            These are the PagerDuty services Metoro can send pages to.
                                        </DialogDescription>

                                        <Button
                                            requiredPermission={{
                                                resource: "integrations/default/*",
                                                operation: "create"
                                            }}
                                            type="submit"
                                            className={"flex flex-col bg-primarytransparent h-full border hover:border-primaryhover border-primary text-white justify-center items-center rounded"}
                                            onClick={() => {
                                                setIsEditing(true)
                                            }}
                                        >Add Service</Button>
                                    </div>
                                    <div
                                        className="min-w-0 bg-backgroundmedium border rounded min-h-0 w-full flex flex-col grow shrink overflow-y-auto">
                                        <div
                                            className="w-full h-[48px] px-4 py-2 rounded-tl rounded-tr justify-start items-start gap-4 flex border-b">
                                            <div
                                                className="h-full w-[100px] font-normal leading-8 text-textmedium text-xl">ID
                                            </div>
                                            <div
                                                className="h-full w-[100px] font-normal leading-8 text-textmedium text-xl">Name
                                            </div>
                                        </div>
                                        {selectedServices.map((service, index) => {
                                            return <div className={"flex justify-end mr-4"}>
                                                <div key={index}
                                                     className="w-full px-4 py-2 border-b border-backgroundmedium hover:bg-backgrounddark flex justify-start items-center gap-4">
                                                    <div
                                                        className="h-full w-[100px] font-normal leading-8 text-textmedium">{service.id}</div>
                                                    <div
                                                        className="h-full w-[100px] font-normal leading-8 text-textmedium">{service.name}</div>
                                                </div>
                                                <div className={"flex items-center justify-center"}>
                                                    <Button
                                                        requiredPermission={{
                                                            resource: "integrations/default/*",
                                                            operation: "delete"
                                                        }}
                                                        onClick={() => {
                                                            setIsEditing(true)
                                                            setSelectedServices((prev) => {
                                                                return prev.filter((value) => value.id !== service.id)
                                                            })
                                                        }}
                                                        className="flex flex-col bg-red-500/20 border border-red-500 text-white justify-center items-center rounded">Remove
                                                    </Button>
                                                </div>
                                            </div>
                                        })}
                                    </div>
                                    {isEditing && <PagerDutyServices allServices={allServices} open={popoverOpen}
                                                                     setOpen={setPopoverOpen}
                                                                     selectedServices={selectedServices}
                                                                     setSelectedServices={setSelectedServices}/>}
                                </DialogContent>
                            </Dialog>
                            <Dialog open={disconnect}>
                                <DialogTrigger asChild>
                                    <Button
                                        requiredPermission={{
                                            resource: "integrations/default/*",
                                            operation: "delete"
                                        }}
                                        onClick={() => setDisconnect(true)}
                                        className={"bg-red-500/20 h-full border border-red-500 text-white flex flex-col justify-center p-2 rounded"}>Disconnect</Button>
                                </DialogTrigger>
                                <DialogContent className="sm:max-w-[825px] bg-background"
                                               onBlur={() => setDisconnect(false)}>
                                    <DialogHeader>

                                        <div className={"flex justify-between items-center mb-4"}>
                                            <DialogTitle className={"text-textmedium font-normal text-center"}>
                                                Are you sure you want to delete PagerDuty integration?
                                            </DialogTitle>
                                            <XIcon
                                                className={"text-textdark hover:cursor-pointer hover:text-primary justify-end"}
                                                onClick={() => setDisconnect(false)}/>
                                        </div>
                                    </DialogHeader>
                                    <DialogDescription className={"text-textdark"}>
                                        You will need to re-add the integration to receive pages for Metoro alerts. If
                                        you
                                        have any alerts that are configured to send pages to PagerDuty, they will no
                                        longer be sent.
                                    </DialogDescription>
                                    <Button type="submit"
                                            className={"flex flex-col bg-red-500/20 h-full border border-red-500 text-textlight justify-end rounded"}
                                            onClick={() => {
                                                axios.post("/api/v1/integration/remove?type=pagerDuty").then(() => {
                                                    setPagerDutyIntegrationExists(false)
                                                    setDisconnect(false)
                                                }).catch((e) => {
                                                    console.error(e)
                                                })
                                            }}>Delete Integration</Button>
                                </DialogContent>
                            </Dialog>
                        </div>
                    }
                </div>
            </div>
        </div>
    );
}

function UptimeMonitorIntegration() {
    const [open, setOpen] = React.useState(false);
    const [openListModal, setOpenListModal] = React.useState(false);
    const [monitors, setMonitors] = React.useState<UptimeMonitor[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [editingMonitor, setEditingMonitor] = React.useState<UptimeMonitor | null>(null);

    useEffect(() => {
        loadMonitors();
    }, []);


    const loadMonitors = () => {
        setLoading(true);
        axios.get("/api/v1/uptimeMonitor/list")
            .then((response) => {
                setMonitors(response.data.uptimeMonitors);
            })
            .catch((e) => {
                console.error(e);
                toast({
                    title: "Error",
                    className: "text-textlight",
                    description: "Failed to load uptime monitors",
                    variant: "destructive",
                });
            })
            .finally(() => setLoading(false));
    };

    const handleSubmit = async (data: UptimeMonitor) => {
        try {
            if (data.uuid) {
                const response = await axios.put(`/api/v1/uptimeMonitor/update?uuid=${encodeURIComponent(data.uuid)}`, data);
                toast({
                    title: "Success",
                    className: "text-textlight",
                    description: "Uptime monitor updated successfully",
                });
                loadMonitors();
                setOpen(false);
                setEditingMonitor(null);
                return response;
            } else {
                const response = await axios.post("/api/v1/uptimeMonitor/create", data);
                toast({
                    title: "Success",
                    className: "text-textlight",
                    description: "Uptime monitor added successfully",
                });
                loadMonitors();
                setOpen(false);
                setEditingMonitor(null);
                return response;
            }
        } catch (error) {
            console.error(error);
            // Let the error bubble up to be handled by the form component
            throw error;
        }
    };

    const deleteMonitor = async (uuid: string) => {
        try {
            await axios.delete(`/api/v1/uptimeMonitor/delete?uuid=${encodeURIComponent(uuid)}`);
            toast({
                title: "Success",
                className: "text-textlight",
                description: "Uptime monitor deleted successfully",
            });
            loadMonitors();
        } catch (e) {
            console.error(e);
            toast({
                title: "Error",
                className: "text-textlight",
                description: "Failed to delete uptime monitor",
                variant: "destructive",
            });
        }
    };

    return (
        <div className={"mt-4 p-4 bg-backgroundlight border rounded"}>
            <div className={"flex"}>
                <div className={"flex-grow"}>
                    <div className={"flex col"}>
                        <div className="w-7 h-7 mr-1 text-primary flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                                 stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                 className="w-5 h-5">
                                <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
                            </svg>
                        </div>
                        <h3 className={"text-lg font-bold"}>Uptime Monitoring</h3>
                    </div>
                    <p className={"text-sm text-textdark"}>Monitor the health and availability of your endpoints</p>
                </div>
                <div className="space-x-2">
                    <Button

                        requiredPermission={{
                            resource: "integrations/default/*",
                            operation: "read"
                        }}
                        onClick={() => setOpenListModal(true)}
                        className={"bg-primarytransparent border border-primary text-textlight hover:text-white"}
                    >
                        View Monitors
                    </Button>
                    <Button
                        requiredPermission={{
                            resource: "integrations/default/*",
                            operation: "create"
                        }}
                        onClick={() => setOpen(true)}
                        className={"bg-primarytransparent border border-primary text-textlight hover:text-white"}
                    >
                        Add Monitor
                    </Button>
                </div>
            </div>

            {/* Add Monitor Dialog */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className={"w-1/2"}>
                    <DialogHeader>
                        <DialogTitle className={"text-textmedium"}>Add Uptime Monitor</DialogTitle>
                        <DialogDescription className={"text-textdark"}>
                            Configure an uptime monitor to track endpoint health
                        </DialogDescription>
                    </DialogHeader>
                    <UptimeMonitorForm onSubmit={handleSubmit}/>
                </DialogContent>
            </Dialog>

            {/* List Monitors Dialog */}
            <Dialog open={openListModal} onOpenChange={setOpenListModal}>
                <DialogContent className={"w-2/3 max-h-[66vh] overflow-y-auto"}>
                    <DialogHeader>
                        <DialogTitle className={"text-textmedium"}>Uptime Monitors</DialogTitle>
                        <DialogDescription className={"text-textdark"}>
                            Manage your uptime monitors
                        </DialogDescription>
                    </DialogHeader>
                    {loading ? (
                        <div className="flex justify-center p-4">
                            <LoadingSpinner/>
                        </div>
                    ) : (
                        <div className="mt-4 space-y-4 min-h-0">
                            {monitors.length === 0 ? (
                                <p className="text-center text-sm text-textdark">No uptime monitors configured</p>
                            ) : (
                                <div className="space-y-4 h-full overflow-y-auto">
                                    {monitors.map((monitor) => (
                                        <div key={monitor.uuid} className="p-4 border rounded bg-backgroundlight">
                                            <div className="flex items-start justify-between">
                                                <div>
                                                    <h4 className="font-medium text-textmedium">{monitor.name}</h4>
                                                    <div className="mt-2">
                                                        <p className="text-sm font-medium text-textmedium">Check Type:</p>
                                                        <p className="text-sm text-textdark">{monitor.checkType}</p>
                                                    </div>
                                                    {monitor.checkType === CheckType.HTTP ? (
                                                        // HTTP specific details
                                                        <>
                                                            <p className="text-sm text-textdark mt-1">{monitor.endpoint}</p>
                                                            <div className="mt-2">
                                                                <p className="text-sm font-medium text-textmedium">Method:</p>
                                                                <p className="text-sm text-textdark">{monitor.method}</p>
                                                            </div>
                                                            <div className="mt-2">
                                                                <p className="text-sm font-medium text-textmedium">Expected Status:</p>
                                                                <p className="text-sm text-textdark">{monitor.expectedStatusCode}</p>
                                                            </div>
                                                            {Object.keys(monitor.headers).length > 0 && (
                                                                <div className="mt-2">
                                                                    <p className="text-sm font-medium text-textmedium">Headers:</p>
                                                                    <div className="mt-1 space-y-1">
                                                                        {Object.entries(monitor.headers).map(([key, value]) => (
                                                                            <p key={key} className="text-sm text-textdark">
                                                                                {key}: {value}
                                                                            </p>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {monitor.body && (
                                                                <div className="mt-2">
                                                                    <p className="text-sm font-medium text-textmedium">Request Body:</p>
                                                                    <p className="text-sm text-textdark mt-1 font-mono bg-backgrounddark p-2 rounded">
                                                                        {monitor.body}
                                                                    </p>
                                                                </div>
                                                            )}
                                                            {monitor.responseBodyRegex && (
                                                                <div className="mt-2">
                                                                    <p className="text-sm font-medium text-textmedium">Response Body Pattern:</p>
                                                                    <p className="text-sm text-textdark mt-1 font-mono rounded">
                                                                        {monitor.responseBodyRegex}
                                                                    </p>
                                                                </div>
                                                            )}
                                                        </>
                                                    ) : (
                                                        // TCP specific details
                                                        <>
                                                            <div className="mt-2">
                                                                <p className="text-sm font-medium text-textmedium">Host:</p>
                                                                <p className="text-sm text-textdark">{monitor.host}</p>
                                                            </div>
                                                            <div className="mt-2">
                                                                <p className="text-sm font-medium text-textmedium">Port:</p>
                                                                <p className="text-sm text-textdark">{monitor.port}</p>
                                                            </div>
                                                        </>
                                                    )}
                                                    <div className="mt-2">
                                                        <p className="text-sm font-medium text-textmedium">Check Frequency:</p>
                                                        <p className="text-sm text-textdark">
                                                            {monitor.checkFrequencySeconds === 60 && "Every minute"}
                                                            {monitor.checkFrequencySeconds === 300 && "Every 5 minutes"}
                                                            {monitor.checkFrequencySeconds === 900 && "Every 15 minutes"}
                                                            {monitor.checkFrequencySeconds === 1800 && "Every 30 minutes"}
                                                            {monitor.checkFrequencySeconds === 3600 && "Every hour"}
                                                        </p>
                                                    </div>
                                                    <div className="mt-2">
                                                        <p className="text-sm font-medium text-textmedium">Metric Names:</p>
                                                        <div className="text-sm text-textdark mt-1 font-mono">
                                                            <p>Response Time: {monitor.responseTimeMetricName}</p>
                                                            <p>Status: {monitor.statusMetricName}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex gap-2">
                                                    <>
                                                        <Button
                                                            requiredPermission={{
                                                                resource: "integrations/default/*",
                                                                operation: "update"
                                                            }}
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => {
                                                                setEditingMonitor(monitor);
                                                                setOpenListModal(false);
                                                                setOpen(true);
                                                            }}
                                                            className="border border-primary text-textlight hover:text-white bg-primarytransparent"
                                                        >
                                                            Edit
                                                        </Button>
                                                        <Button
                                                            requiredPermission={{
                                                                resource: "integrations/default/*",
                                                                operation: "delete"
                                                            }}
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => deleteMonitor(monitor.uuid)}
                                                            className="border border-red-500 text-textlight hover:text-white bg-red-500/10"
                                                        >
                                                            Delete
                                                        </Button>
                                                    </>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Edit Monitor Dialog */}
            <Dialog open={!!editingMonitor} onOpenChange={(open) => !open && setEditingMonitor(null)}>
                <DialogContent className={"w-1/2"}>
                    <DialogHeader>
                        <DialogTitle className={"text-textmedium"}>Edit Uptime Monitor</DialogTitle>
                        <DialogDescription className={"text-textdark"}>
                            Update the configuration for this uptime monitor
                        </DialogDescription>
                    </DialogHeader>
                    {editingMonitor && (
                        <UptimeMonitorForm
                            initialData={editingMonitor}
                            onSubmit={handleSubmit}
                            isEditing={true}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

interface UserSettings {
    dateTimeFormat: DateTimeFormat
    defaultTimeRange?: string
    name?: string
    email?: string
}

// Maybe ensure that display values are like this but actual value set is the formatstr that format will accept.
export enum DateTimeFormat {
    YYYYMMDD = "yyyy/MM/dd HH:mm:ss.SSS", // iso8601, if the user has not specified, this is the default value.
    MMDDYYYY = "MM/dd/yyyy HH:mm:ss.SSS", // common in US
    DDMMYYYY = "dd/MM/yyyy HH:mm:ss.SSS",  // common in EU
}

const DateTimeFormatToDisplayValues = new Map<DateTimeFormat, string>(
    [
        [DateTimeFormat.YYYYMMDD, "yyyy/MM/DD"],
        [DateTimeFormat.MMDDYYYY, "MM/DD/yyyy"],
        [DateTimeFormat.DDMMYYYY, "DD/MM/yyyy"]
    ]
);
export const ISO8601 = "iso8601"
export const DateTimeFormatToLocale = new Map<DateTimeFormat, string>(
    [
        [DateTimeFormat.YYYYMMDD, "ja-JP"], // en-SE is the closest locale to ISO8601
        [DateTimeFormat.MMDDYYYY, "en-US"],
        [DateTimeFormat.DDMMYYYY, "en-GB"]
    ]
);


function UserSettings() {
    const [settings, setSettings] = React.useState<UserSettings>()
    React.useEffect(() => {
        axios.get("/api/v1/user/settings").then((response) => {
            setSettings(response.data)
            localStorage.setItem("dateTimeFormat", response.data.dateTimeFormat)
            localStorage.setItem("defaultTimeRange", response.data.defaultTimeRange)
        }).catch((e) => {
            console.error(e)
        })
    }, [])

    if (settings === undefined) {
        return <div className={"m-4"}>
            <LoadingSpinner className={"absolute top-1/2 left-1/2"}/>
        </div>
    }

    const tabData = [
        {
            title: "Profile Details",
            content: <ProfileSettings setSettings={setSettings} settings={settings}/>
        },
        {
            title: "Authentication",
            content: <ChangePassword/>
        },
        {
            title: "Display",
            content: (
                <div className="flex flex-col gap-6">
                    <DateSetting setSettings={setSettings} settings={settings}/>
                    <Separator/>
                    <DefaultTimeRangeSetting setSettings={setSettings} settings={settings}/>
                </div>
            )
        },
        {
            title: "API Access",
            content: <ShowServiceToken/>
        }
    ]

    return (
        <div className="flex flex-col space-y-4 p-4">
            <h2 className="text-xl font-bold">User Settings</h2>
            <div className="text-textdark">
                Manage your personal settings, authentication options, and API access.
            </div>
            <div className="mt-4">
                <VerticalTabs data={tabData}/>
            </div>
        </div>
    )
}

function ProfileSettings(props: {
    settings: UserSettings,
    setSettings: React.Dispatch<React.SetStateAction<UserSettings | undefined>>
}) {
    const [firstName, setFirstName] = React.useState('');
    const [lastName, setLastName] = React.useState('');
    const [isLoading, setIsLoading] = React.useState(false);
    const [isSaved, setIsSaved] = React.useState(false);

    React.useEffect(() => {
        if (props.settings.name) {
            const nameParts = props.settings.name.split(' ');
            setFirstName(nameParts[0] || '');
            setLastName(nameParts.slice(1).join(' ') || '');
        }
    }, [props.settings.name]);

    const handleSave = async () => {
        // Trim individual parts first, then combine
        const trimmedFirst = firstName.trim();
        const trimmedLast = lastName.trim();
        const fullName = `${trimmedFirst} ${trimmedLast}`.trim();
        
        // Frontend validation matching backend
        if (fullName.length === 0) {
            toast({
                title: "Invalid name",
                description: "Name cannot be empty or contain only whitespace.",
                variant: "destructive"
            });
            return;
        }
        
        if (fullName.length > 255) {
            toast({
                title: "Invalid name",
                description: "Name cannot exceed 255 characters.",
                variant: "destructive"
            });
            return;
        }
        
        setIsLoading(true);
        
        try {
            await axios.post('/api/v1/user/settings', {
                dateTimeFormat: props.settings.dateTimeFormat,
                defaultTimeRange: props.settings.defaultTimeRange,
                name: fullName
            });
            
            props.setSettings({
                ...props.settings,
                name: fullName
            });
            
            setIsSaved(true);
            setTimeout(() => {
                setIsSaved(false);
            }, 2000);
            
            toast({
                title: "Success",
                description: "Profile updated successfully",
            });
        } catch (error: any) {
            const errorMessage = error.response?.data?.error || "Failed to update profile";
            toast({
                title: "Error",
                description: errorMessage,
                variant: "destructive"
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div>
            <div className="font-semibold text-lg mb-4">Profile Information</div>
            <div className="text-textmedium text-wrap mb-4">
                Update your personal information
            </div>
            <div className="space-y-4">
                <div className="space-y-2">
                    <label className="text-sm font-medium text-textdark">Email</label>
                    <Input
                        value={props.settings.email || ''}
                        disabled
                        className="w-[650px] bg-backgrounddark"
                    />
                </div>
                <div className="space-y-2">
                    <label className="text-sm font-medium text-textdark">First Name</label>
                    <Input
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="w-[650px]"
                        placeholder="Enter your first name"
                    />
                </div>
                <div className="space-y-2">
                    <label className="text-sm font-medium text-textdark">Last Name</label>
                    <Input
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="w-[650px]"
                        placeholder="Enter your last name"
                    />
                </div>
                <Button
                    onClick={handleSave}
                    disabled={isLoading}
                    className="w-[650px] border border-primary hover:cursor-pointer bg-primarytransparent rounded text-textmedium hover:text-textlight"
                >
                    {isSaved ? (
                        <span className="flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                            </svg>
                        </span>
                    ) : isLoading ? 'Saving...' : 'Save'}
                </Button>
            </div>
        </div>
    );
}

function ShowServiceToken() {
    const [showServiceToken, setShowServiceToken] = React.useState(false)
    const [showCopyTick, setShowCopyTick] = React.useState(false)
    let serviceToken = window.localStorage.getItem('serviceToken');
    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text)
            .then(() => {
                setShowCopyTick(true)
                setTimeout(() => {
                    setShowCopyTick(false)
                }, 2000)
            })
            .catch((err) => console.error('Failed to copy:', err));
    };

    if (serviceToken == null) {
        return <></>
    }

    return <div>
        <div className={"flex flex-col gap-2"}>
            <h3 className={"text-lg font-semibold"}>Service Token</h3>
            <div className={"text-textmedium text-wrap mb-4"}>
                <div>This token allows you to make direct requests to the Metoro API.</div>
                <div>Do not share this token with anyone. It is the same as sharing your password.</div>
                <div className={"flex items-center gap-2 mt-4"}>
                    <div className={"flex flex-grow gap-2"}>
                        <Input
                            type={showServiceToken ? "text" : "password"}
                            value={serviceToken}
                            readOnly
                            className={"pr-20 py-2 w-[490px]"}
                        />
                        <Button
                            onClick={() => setShowServiceToken(!showServiceToken)}
                            className={"rounded border border-primary bg-primarytransparent"}
                        >
                            {showServiceToken ? "Hide" : "Show"}
                        </Button>
                        <Button
                            onClick={() => copyToClipboard(serviceToken)}
                            className={"rounded border border-primary bg-primarytransparent"}
                        >
                            {showCopyTick ? "Copied!" : "Copy"}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    </div>
}


function DefaultTimeRangeSetting(props: {
    settings: UserSettings,
    setSettings: React.Dispatch<React.SetStateAction<UserSettings | undefined>>
}) {
    const [isLoading, setIsLoading] = React.useState(false);

    const timeRangeOptions = [
        {value: "not-set", label: "Not set"},
        {value: "5m", label: "Last 5 Minutes"},
        {value: "15m", label: "Last 15 Minutes"},
        {value: "30m", label: "Last 30 Minutes"},
        {value: "1h", label: "Last 1 Hour"},
        {value: "3h", label: "Last 3 Hours"},
        {value: "6h", label: "Last 6 Hours"},
        {value: "12h", label: "Last 12 Hours"},
        {value: "24h", label: "Last 24 Hours"},
        {value: "2d", label: "Last 2 Days"},
        {value: "7d", label: "Last 7 Days"},
        {value: "30d", label: "Last 30 Days"}
    ];

    const handleTimeRangeChange = async (value: string) => {
        setIsLoading(true);
        try {
            await axios.post('/api/v1/user/settings', {
                dateTimeFormat: props.settings.dateTimeFormat,
                defaultTimeRange: value
            });
            props.setSettings({
                ...props.settings,
                defaultTimeRange: value
            });
            toast({
                title: "Success",
                description: "Default time range updated successfully",
            });
            localStorage.setItem("defaultTimeRange", value)
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to update default time range",
                variant: "destructive"
            });
        }
        setIsLoading(false);
    };

    return (
        <div className={"text-textmedium"}>
            <div className={"text-lg font-bold mb-4"}>Default Time Range
            </div>
            <div className={"text-sm text-muted-foreground mb-4"}>
                Select the default time range for all views
            </div>
            <Select
                value={props.settings.defaultTimeRange}
                onValueChange={handleTimeRangeChange}
                disabled={isLoading}
            >
                <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Select time range"/>
                </SelectTrigger>
                <SelectContent className={"bg-backgroundmedium"}>
                    {timeRangeOptions.map((option) => (
                        <SelectItem className={"text-textmedium"} key={option.value} value={option.value}>
                            {option.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );
}

function DateSetting(props: {
    settings: UserSettings,
    setSettings: React.Dispatch<React.SetStateAction<UserSettings | undefined>>
}) {
    const [isSaved, setIsSaved] = React.useState(false)

    let possibleDateFormats: DropDownItem[] = [];
    DateTimeFormatToDisplayValues.forEach((displayVal, val) => {
        possibleDateFormats.push({displayName: displayVal, value: val} as DropDownItem)
    });

    function saveSettings() {
        if (props.settings?.dateTimeFormat === undefined) {
            return
        }
        axios.post("/api/v1/user/settings", {
            dateTimeFormat: props.settings?.dateTimeFormat,
            defaultTimeRange: props.settings?.defaultTimeRange
        }).then((response) => {
            localStorage.setItem("dateTimeFormat", props.settings.dateTimeFormat)
            setIsSaved(true)
            setTimeout(() => {
                setIsSaved(false)
            }, 2000) // Show tick for 2 seconds
        }).catch((e) => {
            console.error(e)
        })
    }

    const [selected, setSelected] = React.useState<DropDownItem>({
        displayName: DateTimeFormatToDisplayValues.get(props.settings.dateTimeFormat as DateTimeFormat),
        value: props.settings.dateTimeFormat
    } as DropDownItem);

    React.useEffect(() => {
        props.setSettings(prev => {
            return {...prev, dateTimeFormat: selected.value as DateTimeFormat}
        })
    }, [selected]);

    return <div className={"flex flex-col gap-4 items-start rounded overflow-x-hidden"}>
        <div className={"font-semibold text-lg"}> Date Format</div>
        <div className={"text-textmedium text-wrap"}>Metoro will show all dates in the selected format. By
            default, all
            the dates are shown DD/MM/yyyy.
        </div>
        <SingleSelectDropdown selectedItemTitle={"Date Format"} possibleItems={possibleDateFormats}
                              setSelectedItem={setSelected} selectedItem={selected}/>
        <Button
            className={"w-[650px] border border-primary hover:cursor-pointer bg-primarytransparent rounded text-textmedium hover:text-textlight"}
            onClick={() => saveSettings()}>
            {isSaved ? (
                <span className="flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20"
                                 fill="currentColor">
                                <path fillRule="evenodd"
                                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                      clipRule="evenodd"/>
                            </svg>
                        </span>
            ) : ('Save')}
        </Button>
    </div>
}

function ChangePassword() {
    const [currentPassword, setCurrentPassword] = React.useState('');
    const [newPassword, setNewPassword] = React.useState('');
    const [confirmPassword, setConfirmPassword] = React.useState('');
    const [isLoading, setIsLoading] = React.useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (newPassword !== confirmPassword) {
            toast({
                className: "text-textlight",
                variant: "destructive",
                title: "Passwords don't match",
                description: "Please ensure both passwords are identical.",
            });
            return;
        }

        if (newPassword.length < 8) {
            toast({
                className: "text-textlight",
                variant: "destructive",
                title: "Password too short",
                description: "Password must be at least 8 characters long.",
            });
            return;
        }

        setIsLoading(true);

        try {
            const response = await axios.post('/api/v1/account/change-password', {
                current_password: currentPassword,
                new_password: newPassword,
            });

            if (response.status !== 200) {
                throw new Error('Failed to change password');
            }

            toast({
                className: "text-textlight",
                title: "Password Changed Successfully",
                description: "Your password has been updated.",
            });

            // Clear the form
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (error) {
            toast({
                className: "text-textlight",
                variant: "destructive",
                title: "Error",
                description: "Failed to change password. Please check your current password and try again.",
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div>
            <div className={"font-semibold text-lg mb-4"}>Change Password</div>
            <div className={"text-textmedium text-wrap mb-4"}>
                <div>If you are not using SSO, you can change your password here.</div>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                    <Input
                        id="current-password"
                        type="password"
                        placeholder="Current Password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        required
                        className="w-[650px]"
                    />
                </div>
                <div className="space-y-2">
                    <Input
                        id="new-password"
                        type="password"
                        placeholder="New Password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        className="w-[650px]"
                    />
                </div>
                <div className="space-y-2">
                    <Input
                        id="confirm-password"
                        type="password"
                        placeholder="Confirm New Password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        className="w-[650px]"
                    />
                </div>
                <div className={"text-textdark"}>Your password must be at least 8 characters long.</div>

                <Button type="submit" disabled={isLoading}
                        className={"text-textmedium bg-primarytransparent border border-primary hover:text-textlight rounded"}>
                    {isLoading ? "Changing Password..." : "Change Password"}
                </Button>
            </form>
        </div>
    );
}

interface WebhookIntegrationWithUUID {
    uuid: string;
    name: string;
    url: string;
    headers?: {
        [key: string
            ]:
            string
    };
    body?: string;
    method: string;
}

function WebhookIntegration() {
    const [webhooks, setWebhooks] = React.useState<WebhookIntegrationWithUUID[]>([]);
    const [openAddModal, setOpenAddModal] = React.useState(false);
    const [openListModal, setOpenListModal] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const [editingWebhook, setEditingWebhook] = React.useState<WebhookIntegrationWithUUID | null>(null);

    useEffect(() => {
        loadWebhooks();
    }, []);

    const loadWebhooks = () => {
        setLoading(true);
        axios.get("/api/v1/integrations?type=webhook")
            .then((response) => {
                setWebhooks(response.data);
            })
            .catch((e) => {
                console.error(e);
                let errorMessage = "Failed to load webhook integrations";
                if (e.response && e.response.status === 403) {
                    errorMessage = e.response.data.error;
                }
                toast({
                    title: "Failed to Load Webhooks",
                    className: "text-textlight",
                    description: errorMessage,
                    variant: "destructive",
                });
            })
            .finally(() => setLoading(false));
    };

    const deleteWebhook = (uuid: string) => {
        axios.delete(`/api/v1/integration/webhook?uuid=${encodeURIComponent(uuid)}`)
            .then(() => {
                loadWebhooks();
                toast({
                    title: "Success",
                    className: "text-textlight",
                    description: "Webhook deleted successfully",
                });
            })
            .catch((e) => {
                console.error(e);
                let errorMessage = "Failed to delete webhook";
                if (e.response && e.response.status === 403) {
                    errorMessage = e.response.data.error;
                }
                toast({
                    title: "Failed to Delete Webhook",
                    className: "text-textlight",
                    description: errorMessage,
                    variant: "destructive",
                });
            });
    };

    return (
        <div className={"mt-4 p-4 bg-backgroundlight border rounded text-textmedium"}>
            <div className={"flex"}>
                <div className={"flex-grow"}>
                    <div className={"flex gap-1"}>
                        <Webhook className={"text-primary"}/>
                        <h3 className={"text-lg font-bold"}>Webhooks</h3>
                    </div>
                    <p className={"text-sm text-textdark"}>
                        Create webhook integrations to send alert notifications to external services.
                    </p>
                </div>
                <div className="space-x-2">
                    <Button
                        requiredPermission={{
                            resource: "integrations/default/*",
                            operation: "read"
                        }}
                        onClick={() => setOpenListModal(true)}
                        className={"bg-primarytransparent border border-primary text-textlight hover:text-white"}
                    >
                        View Webhooks
                    </Button>
                    <Button
                        requiredPermission={{
                            resource: "integrations/default/*",
                            operation: "create"
                        }}
                        onClick={() => setOpenAddModal(true)}
                        className={"bg-primarytransparent border border-primary text-textlight hover:text-white"}
                    >
                        Add Webhook
                    </Button>
                </div>
            </div>

            {/* Add/Edit Webhook Dialog */}
            <Dialog open={openAddModal || !!editingWebhook} onOpenChange={(open) => {
                if (!open) {
                    setOpenAddModal(false);
                    setEditingWebhook(null);
                }
            }}>
                <DialogContent className={"w-1/2"}>
                    <DialogHeader>
                        <DialogTitle className={"text-textmedium"}>{editingWebhook ? 'Edit' : 'Add'} Webhook Integration</DialogTitle>
                        <DialogDescription className={"text-textdark"}>
                            {editingWebhook ? 'Update' : 'Configure'} a webhook to receive alert notifications
                        </DialogDescription>
                    </DialogHeader>
                    <WebhookForm 
                        initialData={editingWebhook || undefined}
                        onSuccess={() => {
                            setOpenAddModal(false);
                            setEditingWebhook(null);
                            loadWebhooks();
                        }}
                    />
                </DialogContent>
            </Dialog>

            {/* List Webhooks Dialog */}
            <Dialog open={openListModal} onOpenChange={setOpenListModal}>
                <DialogContent className={"w-2/3 max-h-[66vh] overflow-y-auto"}>
                    <DialogHeader>
                        <DialogTitle className={"text-textmedium"}>Webhook Integrations</DialogTitle>
                        <DialogDescription className={"text-textdark"}>
                            Manage your webhook integrations
                        </DialogDescription>
                    </DialogHeader>
                    {loading ? (
                        <div className="flex justify-center p-4">
                            <LoadingSpinner/>
                        </div>
                    ) : (
                        <div className="mt-4 space-y-4 min-h-0">
                            {webhooks.length === 0 ? (
                                <p className="text-center text-sm text-textdark">No webhooks configured</p>
                            ) : (
                                <div className="space-y-4 h-full overflow-y-auto">
                                    {webhooks.map((webhook) => (
                                        <div key={webhook.uuid} className="p-4 border rounded bg-backgroundlight">
                                            <div className="flex items-start justify-between">
                                                <div>
                                                    <h4 className="font-medium text-textmedium">{webhook.name}</h4>
                                                    <p className="text-sm text-textdark mt-1">{webhook.url}</p>
                                                    {webhook.headers && Object.keys(webhook.headers).length > 0 && (
                                                        <div className="mt-2">
                                                            <p className="text-sm font-medium text-textmedium">Headers:</p>
                                                            <div className="mt-1 space-y-1">
                                                                {Object.entries(webhook.headers).map(([key, value]) => (
                                                                    <p key={key} className="text-sm text-textdark">
                                                                        {key}: {value}
                                                                    </p>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {webhook.body && (
                                                        <div className="mt-2">
                                                            <p className="text-sm font-medium text-textmedium">Body
                                                                Template:</p>
                                                            <p className="text-sm text-textdark mt-1 font-mono bg-backgrounddark p-2 rounded">
                                                                {webhook.body}
                                                            </p>
                                                        </div>
                                                    )}
                                                    <p className="text-sm mt-1 font-medium text-textmedium">Method:</p>
                                                    <p className="text-sm text-textdark">{webhook.method}</p>
                                                </div>
                                                <div className="flex space-x-2">
                                                    <Button
                                                        requiredPermission={{
                                                            resource: "integrations/default/*",
                                                            operation: "update"
                                                        }}
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => {
                                                            setEditingWebhook(webhook);
                                                            setOpenListModal(false);
                                                        }}
                                                        className="border border-primary text-textlight hover:text-white bg-primarytransparent"
                                                    >
                                                        Edit
                                                    </Button>
                                                    <Button
                                                        requiredPermission={{
                                                            resource: "integrations/default/*",
                                                            operation: "delete"
                                                        }}
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => deleteWebhook(webhook.uuid)}
                                                        className="border border-red-500 text-textlight hover:text-white bg-red-500/10"
                                                    >
                                                        Delete
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function WebhookForm({
    onSuccess,
    initialData
}: {
    onSuccess: () => void
    initialData?: WebhookIntegrationWithUUID
}) {
    const [name, setName] = React.useState(initialData?.name || "");
    const [url, setUrl] = React.useState(initialData?.url || "");
    const [method, setMethod] = React.useState(initialData?.method || "POST");
    const [headers, setHeaders] = React.useState<{ key: string; value: string }[]>(
        initialData?.headers 
            ? Object.entries(initialData.headers).map(([key, value]) => ({ key, value }))
            : [{key: "Content-Type", value: "application/json"}]
    );
    const [body, setBody] = React.useState(initialData?.body || "");
    const [loading, setLoading] = React.useState(false);

    const httpMethods = ["GET", "POST", "PUT", "PATCH", "DELETE"];

    // Update headers when method changes
    const handleMethodChange = (newMethod: string) => {
        setMethod(newMethod);
        if (newMethod === "POST" || newMethod === "PUT") {
            // Add Content-Type header if it doesn't exist
            if (!headers.some(h => h.key === "Content-Type")) {
                setHeaders([{key: "Content-Type", value: "application/json"}, ...headers]);
            }
        } else {
            // Remove Content-Type header for other methods
            setHeaders(headers.filter(h => h.key !== "Content-Type"));
        }
    };

    const addHeader = () => {
        setHeaders([...headers, {key: "", value: ""}]);
    };

    const updateHeader = (index: number, field: "key" | "value", value: string) => {
        const newHeaders = [...headers];
        newHeaders[index][field] = value;
        setHeaders(newHeaders);
    };

    const removeHeader = (index: number) => {
        // Don't allow removing the Content-Type header for POST/PUT methods
        if (index === 0 && headers[0].key === "Content-Type" && (method === "POST" || method === "PUT")) {
            return;
        }
        const newHeaders = [...headers];
        newHeaders.splice(index, 1);
        setHeaders(newHeaders);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        const headerMap = headers.reduce((acc, {key, value}) => {
            if (key && value) {
                acc[key] = value;
            }
            return acc;
        }, {} as { [key: string]: string });

        const webhookData = {
            name,
            url,
            method,
            headers: headerMap,
            body,
        };

        const endpoint = initialData 
            ? `/api/v1/integration/webhook`
            : "/api/v1/integrations";

        const requestData = initialData
            ? {
                uuid: initialData.uuid,
                integrationCredentialsJson: webhookData
            }
            : {
                type: "webhook",
                integrationCredentialsJson: JSON.stringify(webhookData),
            };

        const axiosMethod = initialData ? axios.put : axios.post;

        axiosMethod(endpoint, requestData)
            .then(() => {
                toast({
                    className: "text-textlight",
                    title: "Success",
                    description: `Webhook integration ${initialData ? 'updated' : 'added'} successfully`,
                });
                onSuccess();
            })
            .catch((e) => {
                console.error(e);
                let errorMessage = `Failed to ${initialData ? 'update' : 'add'} webhook integration`;
                if (e.response && e.response.status === 403) {
                    errorMessage = e.response.data.error;
                }
                toast({
                    className: "text-textlight",
                    title: `Failed to ${initialData ? 'Update' : 'Add'} Webhook`,
                    description: errorMessage,
                    variant: "destructive",
                });
            })
            .finally(() => setLoading(false));
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <label className="text-sm font-medium text-textdark">Name</label>
                <Input
                    className={"h-[36px]"}
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="My Webhook"
                />
            </div>
            <div>
                <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-textdark">URL</label>
                    <Tooltip>
                        <TooltipTrigger>
                            <InfoIcon className="h-4 w-4 text-textdark hover:cursor-pointer hover:text-primary"/>
                        </TooltipTrigger>
                        <TooltipContent className="w-96 p-4 text-sm text-textmedium">
                            <div className="space-y-2">
                                <p>You can use template variables in your URL for dynamic values:</p>
                                <ul className="list-disc pl-4 space-y-1">
                                    <li><code>$alert_name</code> - Name of the alert</li>
                                    <li><code>$alert_description</code> - Description of the alert</li>
                                    <li><code>$environment</code> - [Deprecated - use $attributes instead] Environment context (only set for Legacy Log and Pod Restart alerts or if you group by environment)</li>
                                    <li><code>$service</code> - [Deprecated - use $attributes instead] Service associated with the alert  (only set for Legacy Log and Pod Restart alerts or if you group by service name)</li>
                                    <li><code>$fired_at</code> - Unix Timestamp when alert was fired</li>
                                    <li><code>$resolved_at</code> - Unix Timestamp when alert was resolved</li>
                                    <li><code>$breaching_datapoint_value</code> - Value that triggered the alert</li>
                                    <li><code>$breaching_datapoint_time</code> - Unix Timestamp of the breaching value</li>
                                    <li><code>$metric_name</code> - Name of the metric</li>
                                    <li><code>$attributes</code> - List of attribute key and value pairs in (key: value format) for which the alert is firing for. Only set when you use a group by in your alert.</li>
                                </ul>
                                <p className="text-xs mt-2">Example: <code>https://api.example.com/webhook?alert=$alert_name&value=$breaching_datapoint_value</code>
                                </p>
                            </div>
                        </TooltipContent>
                    </Tooltip>
                </div>
                <Input
                    className={"h-[36px]"}
                    required
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://api.example.com/webhook?alert=$alert_name"
                />
            </div>
            <div>
                <label className="text-sm font-medium text-textdark">HTTP Method</label>
                <Select value={method} onValueChange={handleMethodChange}>
                    <SelectTrigger className="w-full border-border text-textmedium rounded">
                        <SelectValue placeholder="Select HTTP method"/>
                    </SelectTrigger>
                    <SelectContent className={"bg-background text-textmedium"}>
                        {httpMethods.map((m) => (
                            <SelectItem key={m} value={m}
                                        className={"hover:border hover:border-secondary hover:bg-secondarytransparenter hover:cursor-pointer"}>
                                {m}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            <div>
                <label className="text-sm font-medium text-textdark">Headers</label>
                <div className={"flex flex-col gap-1"}>
                    {headers.map((header, index) => (
                        <div key={index} className="flex gap-2">
                            <Input
                                disabled={header.key === "Content-Type" && (method === "POST" || method === "PUT")}
                                className={"h-[36px]"}
                                value={header.key}
                                onChange={(e) => updateHeader(index, "key", e.target.value)}
                                placeholder="Header name"
                            />
                            <Input
                                className={"h-[36px]"}
                                value={header.value}
                                onChange={(e) => updateHeader(index, "value", e.target.value)}
                                placeholder="Header value"
                            />
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeHeader(index)}
                            >
                                <XIcon className="h-4 w-4 text-textdark hover:cursor-pointer hover:text-primary"/>
                            </Button>
                        </div>
                    ))}
                </div>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addHeader}
                    className="mt-2 text-textmedium bg-primarytransparent border border-primary hover:text-textlight rounded"
                >
                    Add Header
                </Button>
            </div>
            <div>
                <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-textdark">Body Template (Optional)</label>
                    <Tooltip>
                        <TooltipTrigger>
                            <InfoIcon className="h-4 w-4 text-textdark hover:cursor-pointer hover:text-primary"/>
                        </TooltipTrigger>
                        <TooltipContent className="w-96 p-4 text-sm text-textmedium">
                            <div className="space-y-2">
                                <p>You can use the following template variables in your webhook body:</p>
                                <ul className="list-disc pl-4 space-y-1">
                                    <li><code>$alert_name</code> - Name of the alert</li>
                                    <li><code>$alert_description</code> - Description of the alert</li>
                                    <li><code>$environment</code> - [Deprecated - use $attributes instead] Environment
                                        context (only set for Legacy Log and Pod Restart alerts or if you group by
                                        environment)
                                    </li>
                                    <li><code>$service</code> - [Deprecated - use $attributes instead] Service
                                        associated with the alert (only set for Legacy Log and Pod Restart alerts or if
                                        you group by service name)
                                    </li>
                                    <li><code>$fired_at</code> - Unix Timestamp when alert was fired</li>
                                    <li><code>$resolved_at</code> - Unix Timestamp when alert was resolved</li>
                                    <li><code>$breaching_datapoint_value</code> - Value that triggered the alert</li>
                                    <li><code>$breaching_datapoint_time</code> - Unix Timestamp of the breaching value
                                    </li>
                                    <li><code>$metric_name</code> - Name of the metric</li>
                                    <li><code>$attributes</code> - List of attribute key and value pairs (in key: value format) for which the
                                        alert is firing for. Only set when you use a group by in your alert.
                                    </li>
                                </ul>
                                <div className="mt-2 space-y-2">
                                    <p className="font-medium">Examples:</p>
                                    <div className="space-y-1 text-xs">
                                        <p>JSON format:</p>
                                        <code
                                            className="block p-1 bg-backgrounddark rounded">{"{ \"alert\": \"$alert_name\", \"value\": \"$breaching_datapoint_value\" }"}</code>
                                        <p>Plain text format:</p>
                                        <code className="block p-1 bg-backgrounddark rounded">Alert $alert_name was
                                            triggered with value $breaching_datapoint_value</code>
                                        <p>XML format:</p>
                                        <code
                                            className="block p-1 bg-backgrounddark rounded">{"<alert><name>$alert_name</name><value>$breaching_datapoint_value</value></alert>"}</code>
                                    </div>
                                </div>
                            </div>
                        </TooltipContent>
                    </Tooltip>
                </div>
                <textarea
                    className="w-full p-2 border rounded bg-backgrounddark text-textmedium"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder="Enter your custom template (e.g., Alert $alert_name was triggered with value $breaching_datapoint_value)"
                    rows={4}
                />
            </div>
            <Button type="submit" disabled={loading}
                    className={"text-textmedium bg-primarytransparent border border-primary hover:text-textlight rounded"}>
                {loading ? (initialData ? "Updating..." : "Adding...") : (initialData ? "Update Webhook" : "Add Webhook")}
            </Button>
        </form>
    );
}

function GithubIntegration() {
    const [githubIntegrationExists, setGithubIntegrationExists] = React.useState(false);
    const [disconnect, setDisconnect] = React.useState(false);

    React.useEffect(() => {
        axios.get("/api/v1/integrationExists?type=githubV2").then((response) => {
            setGithubIntegrationExists(response.data.exists);
        }).catch((e) => {
            console.error(e);
            let errorMessage = "Failed to check GitHub integration";
            if (e.response && e.response.status === 403) {
                errorMessage = e.response.data.error;
            }
            toast({
                title: "Failed to Check GitHub Integration",
                description: errorMessage,
                variant: "destructive"
            });
        });
    }, []);

    return (
        <div className={"mt-4 p-4 bg-backgroundlight border rounded"}>
            <div className={"flex"}>
                <div className={"flex-grow"}>
                    <div className={"flex col"}>
                        <svg className="w-7 h-7 mr-1 text-primary" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2C6.477 2 2 6.477 2 12C2 16.418 4.865 20.167 8.84 21.565C9.34 21.66 9.525 21.342 9.525 21.064C9.525 20.814 9.517 20.152 9.512 19.302C6.73 19.918 6.14 17.997 6.14 17.997C5.685 16.813 5.028 16.495 5.028 16.495C4.125 15.861 5.095 15.874 5.095 15.874C6.1 15.944 6.63 16.926 6.63 16.926C7.52 18.545 8.97 18.044 9.54 17.777C9.63 17.1 9.89 16.602 10.175 16.326C7.95 16.047 5.62 15.156 5.62 11.306C5.62 10.207 6.01 9.309 6.65 8.607C6.535 8.342 6.195 7.338 6.745 6.01C6.745 6.01 7.585 5.731 9.5 7.008C10.295 6.779 11.15 6.665 12 6.661C12.85 6.665 13.705 6.779 14.502 7.008C16.415 5.731 17.255 6.01 17.255 6.01C17.805 7.337 17.465 8.341 17.352 8.607C17.997 9.309 18.38 10.207 18.38 11.306C18.38 15.165 16.048 16.044 13.817 16.318C14.175 16.658 14.5 17.328 14.5 18.35C14.5 19.821 14.487 20.726 14.487 21.064C14.487 21.344 14.67 21.665 15.177 21.563C19.137 20.161 22 16.417 22 12C22 6.477 17.523 2 12 2Z" fill="currentColor"/>
                        </svg>
                        <h3 className={"text-lg font-bold"}>GitHub</h3>
                    </div>
                    <p className={"text-sm text-textdark"}>Integrate with GitHub to allow Metoro to access your repositories.</p>
                </div>
                <div className={"h-full"}>
                    {
                        !githubIntegrationExists &&
                        <Button
                            requiredPermission={{
                                resource: "integrations/default/*",
                                operation: "create"
                            }}
                            className={"bg-primarytransparent h-full border border-primary text-white flex flex-col justify-center p-2 rounded"}
                            // onClick={() => setOpenAddModal(true)}

                            onClick={() => {
                                window.location.href = "https://github.com/apps/metoro-io/installations/new?redirect_uri=" + window.location.protocol + "//" +  window.location.host + "/github-redirect";
                            }}
                        >
                            Connect GitHub
                        </Button>
                    }
                    {
                        githubIntegrationExists &&
                        <Button
                            requiredPermission={{
                                resource: "integrations/default/*",
                                operation: "delete"
                            }}
                            onClick={() => setDisconnect(true)}
                            className={"bg-red-500/20 h-full border border-red-500 text-white flex flex-col justify-center p-2 rounded"}
                        >
                            Disconnect
                        </Button>
                    }
                </div>
            </div>

            {/* Delete GitHub Integration Dialog */}
            <Dialog open={disconnect} onOpenChange={setDisconnect}>
                <DialogContent className="sm:max-w-[425px] bg-background">
                    <DialogHeader>
                        <DialogTitle className={"text-textmedium"}>Delete GitHub Integration</DialogTitle>
                        <DialogDescription className={"text-textdark"}>
                            Are you sure you want to disconnect GitHub integration?
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <p className="text-sm text-textdark">
                            You will need to re-add the integration to access your GitHub repositories.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button
                            onClick={() => {
                                axios.post("/api/v1/integration/remove?type=githubV2").then(() => {
                                    setGithubIntegrationExists(false);
                                    setDisconnect(false);
                                    toast({
                                        title: "Success",
                                        className: "text-textlight",
                                        description: "GitHub integration removed successfully",
                                    });
                                }).catch((e) => {
                                    console.error(e);
                                    let errorMessage = "Failed to remove GitHub integration";
                                    if (e.response && e.response.status === 403) {
                                        errorMessage = e.response.data.error;
                                    }
                                    toast({
                                        title: "Error",
                                        className: "text-textlight",
                                        description: errorMessage,
                                        variant: "destructive",
                                    });
                                });
                            }}
                            className={"bg-red-500/20 border border-red-500 text-white"}
                        >
                            Disconnect
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}


function Settings() {
    const [searchParams, setSearchParams] = useSearchParams();
    const defaultTab = searchParams.get('tab') || 'usersettings';

    const handleTabChange = (value: string) => {
        setSearchParams({ tab: value });
    };

    return (
        <BaseView title={"Settings"} disableClusterSelector={true} disableTimeRange>
            <div className={"m-4 border bg-backgroundmedium flex-col grow shrink text-textmedium overflow-y-auto"}>
                <Tabs defaultValue={defaultTab} className="grow shrink" onValueChange={handleTabChange}>
                    <TabsList className={"grow shrink flex p-1 border-b"}>
                        <TabsTrigger className={"grow shrink"} value="usersettings">User Settings</TabsTrigger>
                        <TabsTrigger className={"grow shrink"} value="users">Users</TabsTrigger>
                        <TabsTrigger className={"grow shrink"} value="integrations">Integrations</TabsTrigger>
                        <TabsTrigger className={"grow shrink"} value="ingestionSettings">Data Ingestion
                            Settings</TabsTrigger>
                        <TabsTrigger className={"grow shrink"} value="clusterSettings">Cluster Settings</TabsTrigger>
                    </TabsList>
                    <TabsContent value="usersettings">
                        <UserSettings/>
                    </TabsContent>
                    <TabsContent value="users">
                        <div className={"m-4"}>
                            <UsersAndPermissionsTab/>
                        </div>
                    </TabsContent>
                    <TabsContent value="integrations">
                        <IntegrationsTab/>
                    </TabsContent>
                    <TabsContent value="ingestionSettings">
                        <IngestionSettings/>
                    </TabsContent>
                    <TabsContent value="clusterSettings">
                        <ClusterSettings/>
                    </TabsContent>
                </Tabs>
            </div>
        </BaseView>
    );
}


export {
    Settings
}
